/**
 * `/assignments` — the student's work across every course they are enrolled in.
 *
 * Ishraf asked for this for the QED demo (2026-07-27), saying a button alone
 * would do. A button that opens nothing is a bad thing to put in front of a
 * prospect who might click it, and the data already existed — published
 * `course_items` of kind `assignment` — so this is the real page, reading
 * `learning.learner.my.assignments` (own active enrolments only).
 *
 * Ordered by deadline, soonest first, with undated work last: a student opens
 * this to answer "what is due next", not to browse a catalogue.
 */
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  RichText,
  Skeleton,
} from '@mangrove/ui';
import { CalendarClock, CheckCircle2, ClipboardList } from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { LearnShell } from '../components/shell.js';
import { SubmitAssignmentDialog } from '../components/assignment/submit-assignment-dialog.js';
import { SubmissionGrade } from '../components/assignment/submission-grade.js';

export const Route = createFileRoute('/assignments/')({
  component: AssignmentsPage,
});

/** "Due today" / "Due in 3 days" / "Overdue by 2 days" / "No due date". */
function dueLabel(iso: string | null): {
  text: string;
  tone: 'overdue' | 'soon' | 'later' | 'none';
} {
  if (!iso) return { text: 'No due date', tone: 'none' };
  const due = new Date(iso);
  const now = new Date();
  const days = Math.round((due.getTime() - now.getTime()) / 86_400_000);
  if (days < 0) {
    const late = Math.abs(days);
    return { text: `Overdue by ${late} day${late === 1 ? '' : 's'}`, tone: 'overdue' };
  }
  if (days === 0) return { text: 'Due today', tone: 'soon' };
  if (days === 1) return { text: 'Due tomorrow', tone: 'soon' };
  if (days <= 7) return { text: `Due in ${days} days`, tone: 'soon' };
  return { text: `Due ${due.toLocaleDateString()}`, tone: 'later' };
}

// Off the generated contract, NOT `ReturnType<typeof useQuery>` — that
// instantiates the generic with its defaults and collapses `data` to `{}`.
type RouterOutputs = inferRouterOutputs<AppRouter>;
type AssignmentRow = RouterOutputs['learning']['learner']['my']['assignments']['rows'][number];

type WorkStatus = 'complete' | 'overdue' | 'due';

/**
 * Ishraf §2.3 / §2.4 — the three buckets a student filters by.
 *
 * Complete wins over a passed deadline: work handed in late is finished, not
 * overdue, and telling a student otherwise is both wrong and demoralising.
 * Anything without a submission path (published course content) can only ever
 * be Due or Overdue.
 */
function statusOf(a: AssignmentRow, now: number): WorkStatus {
  const st = a.submissionStatus;
  if (st === 'submitted' || st === 'graded' || st === 'returned') return 'complete';
  if (a.dueAt && new Date(a.dueAt).getTime() < now) return 'overdue';
  return 'due';
}

const STATUS_LABEL: Record<WorkStatus, string> = {
  complete: 'Complete',
  overdue: 'Overdue',
  due: 'Due',
};

type SortKey = 'date-asc' | 'date-desc' | 'name-asc' | 'name-desc';
const SORT_OPTIONS: ReadonlyArray<{ value: SortKey; label: string }> = [
  { value: 'date-asc', label: 'Deadline: soonest' },
  { value: 'date-desc', label: 'Deadline: latest' },
  { value: 'name-asc', label: 'Name: A–Z' },
  { value: 'name-desc', label: 'Name: Z–A' },
];

const SELECT_CLASS =
  'press min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium';

function AssignmentsPage(): React.JSX.Element {
  const q = trpc.learning.learner.my.assignments.useQuery();
  const allRows = q.data?.rows ?? [];

  const [status, setStatus] = React.useState<'' | WorkStatus>('');
  const [dueBy, setDueBy] = React.useState('');
  const [sort, setSort] = React.useState<SortKey>('date-asc');

  const rows = React.useMemo(() => {
    const now = Date.now();
    let out = allRows;
    if (status) out = out.filter((a) => statusOf(a, now) === status);
    if (dueBy) {
      // Inclusive of the chosen day: a date picker set to Friday should still
      // show work due at 23:59 that Friday. Undated work is excluded — it has
      // no deadline to compare, so it cannot answer "due by".
      const cutoff = new Date(`${dueBy}T23:59:59.999`).getTime();
      out = out.filter((a) => a.dueAt != null && new Date(a.dueAt).getTime() <= cutoff);
    }
    const byName = (a: AssignmentRow, b: AssignmentRow): number => a.title.localeCompare(b.title);
    const byDate = (a: AssignmentRow, b: AssignmentRow): number => {
      // Undated work sorts last in BOTH directions — it is not "the furthest
      // away", it simply has no deadline.
      if (!a.dueAt && !b.dueAt) return byName(a, b);
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return a.dueAt.localeCompare(b.dueAt);
    };
    return [...out].sort((a, b) => {
      switch (sort) {
        case 'date-asc':
          return byDate(a, b);
        case 'date-desc':
          return -byDate(a, b);
        case 'name-asc':
          return byName(a, b);
        case 'name-desc':
          return -byName(a, b);
      }
    });
  }, [allRows, status, dueBy, sort]);

  const filtersActive = status !== '' || dueBy !== '';
  const now = Date.now();

  return (
    <LearnShell>
      <div className="mx-auto w-full max-w-3xl space-y-4 p-4 sm:p-6">
        <header className="space-y-1">
          <h1 className="font-display text-2xl font-bold">Assignments</h1>
          <p className="text-sm text-muted-foreground">
            Work set by your teachers — finished work stays here too.
          </p>
        </header>

        {allRows.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="assignment-filters">
            <div className="space-y-1.5">
              <Label htmlFor="assignment-status">Status</Label>
              <select
                id="assignment-status"
                data-testid="assignment-filter-status"
                className={SELECT_CLASS}
                value={status}
                onChange={(e) => setStatus(e.target.value as '' | WorkStatus)}
              >
                <option value="">All</option>
                <option value="due">Due</option>
                <option value="overdue">Overdue</option>
                <option value="complete">Complete</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assignment-due-by">Deadline</Label>
              <Input
                id="assignment-due-by"
                data-testid="assignment-filter-deadline"
                type="date"
                className="min-h-touch"
                value={dueBy}
                onChange={(e) => setDueBy(e.target.value)}
                aria-label="Show work due on or before this date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assignment-sort">Sort</Label>
              <select
                id="assignment-sort"
                data-testid="assignment-sort"
                className={SELECT_CLASS}
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {filtersActive ? (
          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span data-testid="assignment-count">
              Showing {rows.length} of {allRows.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="press"
              data-testid="assignment-filter-clear"
              onClick={() => {
                setStatus('');
                setDueBy('');
              }}
            >
              Clear filters
            </Button>
          </div>
        ) : null}

        {q.isLoading ? (
          <div className="space-y-3" data-testid="assignments-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : allRows.length === 0 ? (
          <Card>
            <CardContent className="py-2">
              <EmptyState
                icon={ClipboardList}
                title="No assignments yet"
                description="When a teacher sets work for one of your courses, it appears here with its due date."
              />
            </CardContent>
          </Card>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent className="py-2">
              <EmptyState
                icon={ClipboardList}
                title="Nothing matches those filters"
                description="Try a different status or a later deadline."
              />
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3" data-testid="assignments-list">
            {rows.map((a) => {
              const due = dueLabel(a.dueAt);
              const st = statusOf(a, now);
              return (
                <li key={a.id} data-testid="assignment-row" data-status={st}>
                  <Card className="transition-colors hover:border-primary/40">
                    <CardHeader className="pb-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <CardTitle className="text-base">{a.title}</CardTitle>
                          <CardDescription>
                            <Link
                              to="/course/$courseId"
                              params={{ courseId: a.courseId }}
                              className="hover:underline"
                            >
                              {a.courseTitle}
                            </Link>
                          </CardDescription>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                          {/* §2.4 — the status a student is filtering by, said
                              plainly on the card. Complete work keeps its
                              deadline chip too, so "handed in late" is still
                              legible after the fact. */}
                          <Badge
                            variant={
                              st === 'complete'
                                ? 'secondary'
                                : st === 'overdue'
                                  ? 'destructive'
                                  : 'default'
                            }
                            className="gap-1"
                            data-testid="assignment-status-badge"
                          >
                            {st === 'complete' ? (
                              <CheckCircle2 className="size-3" aria-hidden="true" />
                            ) : null}
                            {STATUS_LABEL[st]}
                          </Badge>
                          <Badge
                            variant={
                              due.tone === 'overdue' && st !== 'complete'
                                ? 'destructive'
                                : due.tone === 'soon'
                                  ? 'default'
                                  : 'secondary'
                            }
                            className="gap-1"
                          >
                            <CalendarClock className="size-3" aria-hidden="true" />
                            {due.text}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    {a.body || a.source === 'assignment' ? (
                      <CardContent className="space-y-3 pt-0">
                        {a.body ? (
                          <div className="text-sm text-muted-foreground">
                            <RichText text={a.body} />
                          </div>
                        ) : null}
                        {/* #273 — the control Ishraf found missing. Only rows
                            that came from the `assignments` table can be turned
                            in: the feed also carries published course CONTENT of
                            kind `assignment`, which has no submission row to
                            write to, so a Submit button there would open a
                            dialog that can only fail. */}
                        {a.source === 'assignment' ? (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <SubmitAssignmentDialog
                              assignmentId={a.id}
                              assignmentTitle={a.title}
                              // What the teacher asked for, so the dialog offers
                              // the control they meant. `submissionKind` is
                              // `notNull` on the row and null only on the
                              // `course_item` shape, which cannot reach here.
                              requiredKind={a.submissionKind ?? undefined}
                              submissionStatus={a.submissionStatus}
                              // The caller's OWN previous submission, so
                              // replacing work starts from what they turned in
                              // rather than from an empty box.
                              previousContent={a.submissionContent}
                              previousFileObjectId={a.submissionFileObjectId}
                              submittedAt={a.submittedAt}
                            />
                            {/* The feed now carries the caller's own score and
                                the teacher's comment, so a student who is told
                                their work was marked is also told the mark. */}
                            <SubmissionGrade
                              score={a.score}
                              pointsPossible={a.pointsPossible}
                              feedback={a.feedback}
                              submissionStatus={a.submissionStatus}
                            />
                          </div>
                        ) : null}
                      </CardContent>
                    ) : null}
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </LearnShell>
  );
}
